import { CollectionsList } from '@/app/(dashboard)/insights/collections/data/CollectionsList.query';
import { TeamInsights } from '@/app/(dashboard)/insights/containers/data/TeamInsights.query';
import { CollectionViews } from '@/app/(insight)/insights/collections/[code]/queries/CollectionViews.query';
import { InsightViews } from '@/app/(insight)/insights/[id]/queries/InsightViews.query';

import type { DeleteCollection } from '@/app/(insight)/insights/collections/[code]/data/DeleteCollection.mutation';
import type { UpdateCollectionViews } from '@/app/(insight)/insights/collections/[code]/queries/UpdateCollectionViews.mutation';
import type { UpdateInsight } from '@/app/(insight)/insights/[id]/queries/UpdateInsight.mutation';
import type { UpdateInsightViews } from '@/app/(insight)/queries/UpdateInsightViews.mutation';
import type { View } from '@/app/(insight)/insights/types';
import type { Cache } from '@urql/exchange-graphcache';
import type { ResultOf, VariablesOf } from 'gql.tada';

/**
 * Invalidate every cached arg-set of a Query list field so active queries
 * refetch. Used after creates whose mutation under-selects the list node
 * (e.g. createInsight returns no `_id`) — hand-pushing would inject an
 * incomplete, unkeyed row.
 */
const invalidateListField = (cache: Cache, fieldName: string) => {
  cache
    .inspectFields('Query')
    .filter(field => field.fieldName === fieldName)
    .forEach(field => cache.invalidate('Query', fieldName, field.arguments ?? undefined));
};

export const insightMutationUpdates = {
  createInsight(_result: unknown, _args: unknown, cache: Cache) {
    invalidateListField(cache, 'insights');
  },

  createCollection(_result: unknown, _args: unknown, cache: Cache) {
    invalidateListField(cache, 'collections');
  },

  updateInsight(_result: unknown, args: VariablesOf<typeof UpdateInsight>, cache: Cache) {
    cache
      .inspectFields('Query')
      .filter(field => field.fieldName === 'insights')
      .forEach(field => {
        cache.updateQuery<ResultOf<typeof TeamInsights>, VariablesOf<typeof TeamInsights>>(
          { query: TeamInsights, variables: field.arguments as VariablesOf<typeof TeamInsights> },
          data => {
            // `findIndex` returns 0 for the first match, so guard on `!== -1`
            // rather than truthiness.
            const index = data?.insights.nodes?.findIndex(node => node?._id === args.input.id) ?? -1;
            const node = index !== -1 ? data?.insights.nodes?.[index] : undefined;
            if (node && data?.insights.nodes) {
              data.insights.nodes[index] = { ...node, title: args.input.title ?? null };
            }
            return data;
          },
        );
      });
  },

  deleteCollection(_result: unknown, args: VariablesOf<typeof DeleteCollection>, cache: Cache) {
    cache
      .inspectFields('Query')
      .filter(field => field.fieldName === 'collections')
      .forEach(field => {
        cache.updateQuery<ResultOf<typeof CollectionsList>, VariablesOf<typeof CollectionsList>>(
          { query: CollectionsList, variables: field.arguments as VariablesOf<typeof CollectionsList> },
          data => {
            if (data?.collections.nodes) {
              data.collections.nodes = data.collections.nodes.filter(node => node?._id !== args.id);
            }
            return data;
          },
        );
      });
  },

  updateInsightViews(
    result: ResultOf<typeof UpdateInsightViews>,
    args: VariablesOf<typeof UpdateInsightViews>,
    cache: Cache,
  ) {
    cache.updateQuery<ResultOf<typeof InsightViews>, VariablesOf<typeof InsightViews>>(
      { query: InsightViews, variables: args },
      data => {
        if (result.updateInsightViews && data?.insight) {
          data.insight.views = result.updateInsightViews as View[];
        }
        return data;
      },
    );
  },

  updateCollectionViews(
    result: ResultOf<typeof UpdateCollectionViews>,
    args: VariablesOf<typeof UpdateCollectionViews>,
    cache: Cache,
  ) {
    cache.updateQuery<ResultOf<typeof CollectionViews>, VariablesOf<typeof CollectionViews>>(
      { query: CollectionViews, variables: args },
      data => {
        if (result.updateCollectionViews && data?.collection) {
          data.collection.views = result.updateCollectionViews as View[];
        }
        return data;
      },
    );
  },
};
